import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'rmm-management',
  slug: 'rmm-management',
  name: 'RMM Management Plane',
  description: 'Fleet-wide patch approval rings, monitoring rules and alerts, auto-approval policies, and phased-rollout device targeting for RMM automation.',
  icon: 'Tune',
  endpoints: [
    {
      slug: 'patch-rings-list',
      method: 'GET',
      path: '/api/rmm-patch/rings',
      summary: 'List patch approval rings',
      description: 'Returns the tenant\'s configured patch approval rings (staged rollout waves like test/broad), each with its member device ids and the soak-time hours before a ring is considered cleared for the next one. POST to the same path to create a new ring. With no rings configured yet, this returns representative sample rings and writes nothing (simulation-until-configured).',
      auth: 'session',
      responseExample: `[
  {
    "id": "rmr_7f2a",
    "ring": "test",
    "name": "Ring 0 — Test",
    "deviceIds": ["dev_10ac", "dev_3d19"],
    "soakHours": 48,
    "notes": "Library lab machines; low blast radius.",
    "simulated": false
  }
]`,
      sourceFile: 'routes/rmm-patch.js',
    },
    {
      slug: 'patch-compliance',
      method: 'GET',
      path: '/api/rmm-patch/compliance',
      summary: 'Get fleet patch-compliance rollup',
      description: 'Reporting-only fleet compliance summary for the management console: percent patched, missing-update counts by severity, the oldest still-missing patch\'s age, and how many devices haven\'t checked in recently (counted honestly as stale, never dropped from the denominator). Nothing in rmm-patch applies or triggers an install — the whole module only reports what a collector observed.',
      auth: 'session',
      responseExample: `{
  "deviceCount": 42,
  "devicesWithData": 40,
  "patchedPct": 87.3,
  "missingBySeverity": { "critical": 4, "important": 9, "moderate": 2, "low": 0, "unknown": 1 },
  "oldestMissingDays": 41,
  "totals": { "missing": 16, "installed": 110, "failed": 0, "deferred": 2, "notApplicable": 5 },
  "rebootPending": 5,
  "staleDevices": 3,
  "simulated": false
}`,
      sourceFile: 'routes/rmm-patch.js, lib/rmm-patch.js',
    },
    {
      slug: 'monitoring-rule-create',
      method: 'POST',
      path: '/api/rmm-monitoring/rules',
      summary: 'Create a monitoring rule',
      description: 'Creates a metric-threshold monitoring rule (e.g. disk_free_pct lt 10 for 600 seconds). New rules are created disabled by default — an admin must explicitly enable one, and auto-ticketing only fires when both the module-level and the rule-level autoTicket flags are on. GET the same path to list existing rules.',
      auth: 'session',
      requestBodyExample: `{
  "name": "Low disk free — labs",
  "metric": "disk_free_pct",
  "op": "lt",
  "threshold": 10,
  "severity": "high",
  "forDuration": 600,
  "scope": { "tag": "role:lab" }
}`,
      responseExample: `{
  "id": "rmn_58c1",
  "name": "Low disk free — labs",
  "metric": "disk_free_pct",
  "op": "lt",
  "threshold": 10,
  "severity": "high",
  "forDuration": 600,
  "enabled": false,
  "autoTicket": false,
  "createdAt": "2026-07-21T14:02:00.000Z"
}`,
      sourceFile: 'routes/rmm-monitoring.js, lib/rmm-monitoring.js',
    },
    {
      slug: 'monitoring-alert-ack',
      method: 'POST',
      path: '/api/rmm-monitoring/alerts/:id/ack',
      summary: 'Acknowledge a monitoring alert',
      description: 'Marks an open monitoring alert as acknowledged and stamps the acknowledging admin. This only changes the alert\'s status — it does not resolve the underlying condition or touch any linked ticket.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Alert id.' }],
      responseExample: `{ "ok": true, "status": "ack" }`,
      sourceFile: 'routes/rmm-monitoring.js',
    },
    {
      slug: 'monitoring-alert-ticket',
      method: 'POST',
      path: '/api/rmm-monitoring/alerts/:id/ticket',
      summary: 'Promote an alert to a help-desk ticket',
      description: 'Converts a raised monitoring alert into a help-desk ticket. Nexus treats the alert and the resulting ticket as two views of the same underlying record — the ticket is stamped with the alert\'s id and the alert with the ticket\'s id, so calling this again on an already-promoted alert returns the existing ticket instead of creating a duplicate.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Alert id.' }],
      responseExample: `{ "ok": true, "ticketId": "tkt_9b41" }`,
      sourceFile: 'routes/rmm-monitoring.js',
    },
    {
      slug: 'approval-policies-list',
      method: 'GET',
      path: '/api/rmm-approval-policies',
      summary: 'List patch auto-approval policies',
      description: 'Returns this tenant\'s policy-based auto-approval rules for patch-apply jobs — each policy states the ring/window/severity criteria a candidate job must fully match to skip the manual approval click. POST to the same path to create one. Policies only take effect once the tenant-wide master switch is armed via POST /api/rmm-approval-policies/arm; every other patch-apply gate (signature, capability, open window, kill-switch) still applies regardless of policy coverage.',
      auth: 'session',
      responseExample: `{
  "policies": [
    {
      "id": "pol_1c9a",
      "name": "Low-risk quality updates, broad ring only",
      "enabled": true,
      "criteria": { "rings": ["broad"], "severities": ["moderate", "low"], "allowReboot": false },
      "createdBy": "jchen",
      "createdAt": "2026-07-10T15:20:00.000Z"
    }
  ]
}`,
      sourceFile: 'routes/rmm-approval-policies.js, lib/rmm-approval-policy.js',
    },
    {
      slug: 'approval-policies-evaluate',
      method: 'POST',
      path: '/api/rmm-approval-policies/evaluate',
      summary: 'Dry-run patch auto-approval policies',
      description: 'Evaluates a hypothetical (or, given a deviceId, its real missing-update) patch job against the tenant\'s current approval policies and returns the decision — approves nothing. windowOpen and killActive are assumed favorable so the response isolates the policy-coverage decision from the live gates the real apply/issue path also enforces.',
      auth: 'session',
      requestBodyExample: `{ "deviceId": "pdev_2f61", "ring": "broad", "allowReboot": false }`,
      responseExample: `{
  "simulated": true,
  "updateCount": 2,
  "decision": { "autoApprove": true, "policyId": "pol_1c9a", "reason": "matched" },
  "note": "Dry-run only; approves nothing."
}`,
      sourceFile: 'routes/rmm-approval-policies.js, lib/rmm-approval-policy.js',
    },
    {
      slug: 'targeting-groups-list',
      method: 'GET',
      path: '/api/rmm-targeting/groups',
      summary: 'List device targeting groups',
      description: 'Returns saved device targeting groups used to stage a rollout (patch, script, or config push) across rings of devices instead of the whole fleet at once. POST to the same path to define a new group from a device selector.',
      auth: 'session',
      responseExample: `{
  "groups": [
    { "id": "rmt_grp_44a1", "name": "Front-office workstations", "deviceIds": ["dev_10ac", "dev_88f2"], "createdAt": "2026-07-05T12:00:00.000Z" }
  ]
}`,
      sourceFile: 'routes/rmm-targeting.js, lib/rmm-targeting.js',
    },
  ],
};
